import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Login from './Login';
import { db } from './firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import QuickStats from './QuickStats';
import QuestionFAQ from './QuestionFAQ';
import './Home.css';
import HeroBanner from './HeroBanner';

const StatCard = ({ number, label }) => (
  <div className="home-stat-card">
    <h3>{number}</h3>
    <p>{label}</p>
  </div>
);

const PreviewCard = ({ title, description, type, isPopular = false }) => (
  <div className="home-preview-card">
    {isPopular && <span className="home-popular-badge">Populaire</span>}
    <h4>{title}</h4>
    <p>{description}</p>
    <span className="home-preview-type">{type}</span>
  </div>
);

const Home = ({ user }) => {
  const [firestoreUserData, setFirestoreUserData] = useState(null);
  const [navbarHeight, setNavbarHeight] = useState(0);
  const lastHeightRef = useRef(0);
  const rafRef = useRef(null);

  const formatDate = (date) => date.toISOString().split('T')[0];

  const calculateTotalStats = (dailyActivity) => {
    console.log('Home - dailyActivity reçu:', dailyActivity);

    let totalQuizCount = 0;
    let totalFlashcardsStudied = 0;

    if (dailyActivity) {
      Object.values(dailyActivity).forEach((dailyData) => {
        totalQuizCount += dailyData.quizCount || dailyData.quizzes || 0;
        totalFlashcardsStudied += dailyData.flashcardsStudied || dailyData.flashcards || 0;
      });
    }

    const calculatedStats = {
      quizCount: totalQuizCount,
      flashcardsStudied: totalFlashcardsStudied,
    };

    console.log('Home - Stats totales calculées:', calculatedStats);
    return calculatedStats;
  };

  useEffect(() => {
    if (!user) {
      console.log('Aucun utilisateur connecté');
      return;
    }

    console.log('Données de l\'utilisateur (Firebase Auth) :', {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      emailVerified: user.emailVerified,
      photoURL: user.photoURL,
      providerData: user.providerData,
    });

    const userDocRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        console.log('Données complètes de l\'utilisateur dans Firestore :', data);
        setFirestoreUserData(data);
      } else {
        console.log('Aucun document trouvé dans Firestore pour cet utilisateur');
      }
    }, (error) => {
      console.error('Erreur lors de la récupération des données Firestore :', error.message, error.code);
    });

    return () => unsubscribe();
  }, [user]);

  // Dynamically calculate Navbar height, memoized with useCallback
  const updateNavbarHeight = useCallback(() => {
    const navbar = document.querySelector('.navbar');
    if (navbar) {
      const height = navbar.offsetHeight;
      if (height !== lastHeightRef.current) {
        lastHeightRef.current = height;
        setNavbarHeight(height);
      }
    }

    // Continue checking until the height stabilizes
    rafRef.current = requestAnimationFrame(updateNavbarHeight);
  }, []); // Empty dependency array since setNavbarHeight and lastHeightRef are stable

  const debounceUpdate = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    updateNavbarHeight();
  }, [updateNavbarHeight]); // Add updateNavbarHeight as a dependency

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const navbar = document.querySelector('.navbar');
      if (navbar) {
        debounceUpdate();
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    window.addEventListener('scroll', debounceUpdate);
    window.addEventListener('resize', debounceUpdate);

    return () => {
      observer.disconnect();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      window.removeEventListener('scroll', debounceUpdate);
      window.removeEventListener('resize', debounceUpdate);
    };
  }, [debounceUpdate]);

  const totalStats = calculateTotalStats(firestoreUserData?.activity?.daily);

  const todayDisplay = formatDate(new Date()).split('-').reverse().join('/');
  const monthDisplay = new Date().toLocaleString('fr-FR', { month: 'long', year: 'numeric' });

  return (
    <div
      className="home-container"
      style={{ minHeight: `calc(100vh - ${navbarHeight}px)` }}
    >
      <section id="home-hero-section" className="home-hero-section">
        <HeroBanner user={user} />
      </section>

      <section id="home-welcome-section" className="home-welcome-section">
        <div className="home-welcome-content">
          {user ? (
            <>
              <h2>
                Bon retour,{' '}
                {firestoreUserData?.pseudo
                  ? firestoreUserData.pseudo
                  : user.displayName
                  ? user.displayName
                  : user.email.split('@')[0] || 'Utilisateur'} !
              </h2>
              <p>Préparez-vous à tester vos connaissances et améliorer vos compétences !</p>
              <div className="home-user-stats">
                <p>
                  Total étudié:{' '}
                  <strong>
                    {totalStats.quizCount} quiz,{' '}
                    {totalStats.flashcardsStudied} flashcards
                  </strong>
                </p>
              </div>
            </>
          ) : (
            <>
              <h2>Rejoignez notre communauté d'apprentissage</h2>
              <p>Déjà plus de <strong>5,000+</strong> apprenants nous font confiance</p>
              <Login />
            </>
          )}
        </div>
      </section>

      <section id="home-quiz-section" className="home-quiz-section">
        <div className="home-section-header">
          <h2>Zone de Quiz</h2>
          <span className="home-section-tag">Interactif</span>
        </div>
        <div className="home-section-content">
          <p>Testez vos connaissances avec nos quiz interactifs</p>
          <div className="home-stats-container">
            <StatCard number="7" label="Thèmes disponibles" />
            <StatCard number="6 500+" label="Questions" />
            <StatCard number="20%" label="Taux de réussite moyen" />
          </div>
          <div className="home-previews-container">
            <PreviewCard title="Nature" description="Testez vos connaissances sur les chants d'oiseaux" type="Quiz"/>
            <PreviewCard title="Art" description="Êtes-vous incollables sur les peintures ?" type="Quiz" />
            <PreviewCard title="Géographie Mondiale" description="Tester vos connaissances sur les capitales et les drapeaux" type="Quiz - Nouveau" isPopular={true} />
          </div>
          {user && <Link to="/quizz"><button className="home-action-button">Commencer un Quiz</button></Link>}
        </div>
      </section>

      <section id="home-flashcard-section" className="home-flashcard-section">
        <div className="home-section-header">
          <h2>Apprentissage par Flashcards</h2>
          <span className="home-section-tag">Efficace</span>
        </div>
        <div className="home-section-content">
          <p>Maîtrisez des concepts avec notre système de flashcards intelligent</p>
          <div className="home-stats-container">
            <StatCard number="200+" label="Ensembles de flashcards" />
            <StatCard number="6500+" label="Cartes créées" />
            <StatCard number="90%" label="Taux de mémorisation" />
          </div>
          <div className="home-previews-container">
            <PreviewCard title="Histoire" description="Défiez vous sur les dates des évènements historiques" type="Flashcard" isPopular={true} />
            <PreviewCard title="Science" description="Connaissez vous tous les éléments atomiques ?" type="Flashcard" />
            <PreviewCard title="Capitales Mondiales" description="Mémorisez toutes les capitales du monde" type="Flashcard - Nouveau" />
          </div>
          {user && <Link to="/learn"><button className="home-action-button">Apprendre avec les flashcards</button></Link>}
        </div>
      </section>

      <section id="home-account-section" className="home-account-section">
        <div className="home-section-header">
          <h2>Votre Compte</h2>
          <span className="home-section-tag">Personnalisé</span>
        </div>
        <div className="home-section-content">
          {user ? (
            <>
              <p>Gérez votre profil et suivez vos progrès</p>
              <QuickStats
                userId={user.uid}
                todayDisplay={todayDisplay}
                monthDisplay={monthDisplay}
                onStatsUpdate={(stats) => console.log('Stats from Home:', stats)}
              />
            </>
          ) : (
            <p>Inscrivez-vous pour débloquer toutes les fonctionnalités</p>
          )}
          <Link to="/account"><button className="home-action-button">Voir le Profil</button></Link>
        </div>
      </section>

      <section id="home-pricing-section" className="home-pricing-section">
        <div className="home-section-header">
          <h2>Tarification simple et transparente</h2>
          <span className="home-section-tag">Abordable</span>
        </div>
        <div className="home-pricing-plans">
          <div className="home-pricing-card">
            <h3>Gratuit</h3>
            <p className="home-price">0 € /mois</p>
            <p className="home-description">Parfait pour débuter</p>
            <ul className="home-features-list">
              <li>5 quiz par jour</li>
              <li>5 sessions de flashcards par jour</li>
              <li>Suivi de progression basique</li>
              <li>Support par email</li>
            </ul>
            <Link to="/account"><button className="home-action-button home-secondary">Commencer</button></Link>
          </div>
          <div className="home-pricing-card home-popular">
            <div className="home-popular-tag">Populaire</div>
            <h3>Premium</h3>
            <p className="home-price">2 € /mois</p>
            <p className="home-description">Pour les apprenants sérieux</p>
            <ul className="home-features-list">
              <li>Quiz illimités</li>
              <li>Sessions de flashcards illimités</li>
              <li>Analytique avancée</li>
              <li>Support prioritaire</li>
              <li>Thèmes personnalisés</li>
            </ul>
            <Link to="/account"><button className="home-action-button home-primary">Essayer</button></Link>
          </div>
          <div className="home-pricing-card">
            <h3>Personnalisé</h3>
            <p className="home-price">Sur Devis</p>
            <p className="home-description">Adapté aux institutions</p>
            <ul className="home-features-list">
              <li>Création de quiz sur mesure</li>
              <li>Support dédié</li>
              <li>Gestion d'équipe</li>
              <li>Intégrations avancées</li>
              <li>Sessions de formation</li>
            </ul>
            <Link to="/account"><button className="home-action-button home-secondary">Contacter-Nous</button></Link>
          </div>
        </div>
      </section>

      <section id="home-faq-section" className="home-faq-section">
        <h2>Questions Fréquentes</h2>
        <QuestionFAQ limit={3} />
      </section>

      <section id="home-final-cta" className="home-final-cta">
        <h2>Prêt à améliorer vos connaissances ?</h2>
        <p>Rejoignez des milliers d'apprenants qui progressent chaque jour</p>
        <Link to={user ? "/quizz" : "/account"}>
          <button className="home-action-button home-primary home-large">
            {user ? "Commencer à apprendre" : "S'inscrire gratuitement"}
          </button>
        </Link>
      </section>
    </div>
  );
};

export default Home;